import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  IconButton,
} from "@mui/material";
import { Edit, Save, Delete } from "@mui/icons-material";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { showErrorSnackbar } from "../../../../redux/slices/snackbar";
import { useDialog } from "../../../../contexts/DialogContext";
import DeletePlanModal from "./DeletePlanModal";

const InternetPlanTable = () => {
  const { openDialog } = useDialog();
  const dispatch = useDispatch();
  const { watch, setValue } = useFormContext();

  const allowedPlans = watch("planGroupPlans") || [];
  const serviceType = watch("serviceType");
  const sharingType = watch("sharingType");

  const [editRowId, setEditRowId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Handlers
  const handleEdit = (row) => {
    setEditRowId(row.planId);
    setEditValues(row);
  };

  const handleSave = () => {
    const totalSharing =
      Number(editValues.msoSharing) +
      Number(editValues.distSharing) +
      Number(editValues.lcoSharing);

    if (totalSharing !== 100 && editValues.isSharingAllowed) {
      dispatch(showErrorSnackbar("Sharing should be equal to 100"));
      return;
    }

    const totalOttSharing =
      Number(editValues.ottMsoSharing) +
      Number(editValues.ottDistSharing) +
      Number(editValues.ottLcoSharing);

    if (
      editValues.serviceType === "INTERNET_OTT" &&
      totalOttSharing !== 100 &&
      editValues.isSharingAllowed
    ) {
      dispatch(showErrorSnackbar("OTT sharing should be equal to 100"));
      return;
    }

    const updatedPlans = allowedPlans.map((plan) =>
      plan.planId === editRowId ? { ...editValues } : plan,
    );

    setValue("planGroupPlans", updatedPlans, {
      shouldDirty: true,
      shouldValidate: true,
    });

    setEditRowId(null);
  };

  const handleChange = (e, field) => {
    setEditValues({ ...editValues, [field]: e.target.value });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // reset to first page
  };

  const handleDelete = (planId) => {
    return openDialog(
      "delete_plan",
      "Delete sharing",
      <DeletePlanModal planId={planId} allowedPlans={allowedPlans} setValue={setValue} />,
    );
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Plan Name</strong>
              </TableCell>
              <TableCell>
                <strong>MSO Sharing</strong>
              </TableCell>
              <TableCell>
                <strong>Dist Sharing</strong>
              </TableCell>
              <TableCell>
                <strong>LCO Sharing</strong>
              </TableCell>

              <TableCell>
                <strong>Action</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allowedPlans.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
              <TableRow key={row.planId}>
                <TableCell>{row.planName}</TableCell>

                <TableCell>
                  {editRowId === row.planId ? (
                    <TextField
                      value={editValues.msoSharing}
                      onChange={(e) => handleChange(e, "msoSharing")}
                      size="small"
                    />
                  ) : (
                    `${row.msoSharing ? row.msoSharing : 0}%`
                  )}
                </TableCell>

                <TableCell>
                  {editRowId === row.planId ? (
                    <TextField
                      value={editValues.distSharing}
                      onChange={(e) => handleChange(e, "distSharing")}
                      size="small"
                    />
                  ) : (
                    `${row.distSharing ? row.distSharing : 0}%`
                  )}
                </TableCell>

                <TableCell>
                  {editRowId === row.planId ? (
                    <TextField
                      value={editValues.lcoSharing}
                      onChange={(e) => handleChange(e, "lcoSharing")}
                      size="small"
                    />
                  ) : (
                    `${row.lcoSharing ? row.lcoSharing : 0}%`
                  )}
                </TableCell>

                <TableCell>
                  {editRowId === row.planId ? (
                    <>
                      <IconButton onClick={handleSave} color="primary">
                        <Save />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(row.planId)} color="error">
                        <Delete />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      {sharingType !== "FIXED" && (
                        <IconButton onClick={() => handleEdit(row)} color="secondary">
                          <Edit />
                        </IconButton>
                      )}
                      <IconButton onClick={() => handleDelete(row.planId)} color="error">
                        <Delete />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[50, 100]}
        component="div"
        count={allowedPlans.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};

export default InternetPlanTable;
